import React, { useCallback, useEffect, useRef, useState } from "react";

import { FormattingHelpers, InterpolationType, LayerAPI, LayerUtilsAPI } from "@ai360/core";

import { Checkbox, Loader, SelectInput } from "~/core";
import { ACTIVE_YN } from "~/core/picklist";

import { getUser } from "~/login/selectors";

import {
    actions as recsEventsActions,
    eventsModels,
    eventsSelectors,
    models as recsEventsModel,
} from "~/recs-events";

import { messages } from "~/action-panel/components/event-module/components/event-info/i18n-messages";
import { messages as commonMessages } from "~/action-panel/components/common/rec-event-info/i18n-messages";
import { messages as layerMessages } from "~/action-panel/components/layer-module/components/i18n-messages";
import * as actions from "~/action-panel/components/event-module/components/event-info/actions";
import * as layerSelectors from "~/action-panel/components/layer-module/components/layer-list/selectors";
import * as layerActions from "~/action-panel/components/layer-module/components/layer-list/actions";
import * as eventInfoSelectors from "~/action-panel/components/event-module/components/event-info/selectors";
import * as eventListSelectors from "~/action-panel/components/event-module/components/event-list/selectors";

import { ContextMenu } from "./sampling-zone-context-menu";
import EditLayerProperties from "./edit-layer-properties";
import EditColorRamp from "./edit-color-ramp";
import "~/action-panel/components/common/rec-event-info/rec-event-zone-info.css";
import "./sampling-zone-info.css";
import { CloseIcon } from "~/core/icons";
import { logFirebaseEvent } from "~/utils/firebase";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { useIntl } from "react-intl";
import * as sampleSoilModule from "~/action-panel/components/event-module/components/event-info/components/event-sample-soil/components/event-sample-soil-form";
import * as sampleTissueModule from "~/action-panel/components/event-module/components/event-info/components/event-sample-tissue/components/event-sample-tissue-form";

const SamplingZoneInfo_ = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const { fieldGuidToEventDetails, lastSelectedDisplaySurface, layerNameToSurfaceInfoMap } =
        useAppSelector(eventsSelectors.getModuleState);
    const { eventSummary, isLoading } = useAppSelector(eventInfoSelectors.getModuleState);
    const { fieldGuid } = eventSummary;
    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
    const layerInfos = useAppSelector((state) =>
        layerSelectors.getLayerInfos(state).get(fieldGuid)
    );
    const userInfo = useAppSelector(getUser);
    const loadingSurfacesSet = useAppSelector(layerSelectors.getLoadingSurfacesSet);
    const isMergeEvent = useAppSelector(eventListSelectors.getIsMergeEvent);
    const colorRamps = useAppSelector(layerSelectors.getColorRamps);
    const onFetchLayerNameToSurfaceInfoMap = useCallback(
        (fieldGuid: string) =>
            dispatch(recsEventsActions.fetchLayerNameToSurfaceInfoMap(fieldGuid)),
        [dispatch]
    );
    const onChangeSurfaceLayerProps = useCallback(
        (payload) => dispatch(layerActions.changeSurfaceLayerProps(payload)),
        [dispatch]
    );
    const onChangeManualLayerProps = useCallback(
        (payload) => dispatch(layerActions.changeManualLayerProps(payload)),
        [dispatch]
    );
    const setDataLoading = useCallback(
        (isLoading) => dispatch(actions.setECDataLoading(isLoading)),
        [dispatch]
    );
    const onManualZoneEnabled = useCallback(
        (isManualZone: boolean) => dispatch(recsEventsActions.setIsManualZoneMode(isManualZone)),
        [dispatch]
    );
    const onSetZoneColorRamps = useCallback(
        (manualZoneColorRamps: LayerAPI.IColorRamp[]) =>
            dispatch(recsEventsActions.setManualZoneColorRamps(manualZoneColorRamps)),
        [dispatch]
    );
    const onColorRampSelected = useCallback(
        (colorRamp: string) => dispatch(recsEventsActions.setSelectedColorRampGuid(colorRamp)),
        [dispatch]
    );
    const onSetManualZones = useCallback(
        (fieldGuid) => dispatch(recsEventsActions.setManualZones(fieldGuid)),
        [dispatch]
    );

    const onSetEventZonesFromLayer = useCallback(
        (surfaceInfo: LayerAPI.ISubLayer) =>
            dispatch(recsEventsActions.setEventZonesFromLayer(fieldGuid, surfaceInfo)),
        [dispatch, fieldGuid]
    );
    const onResetZones = useCallback(
        () => dispatch(recsEventsActions.resetEventAreas(fieldGuid)),
        [dispatch, fieldGuid]
    );
    const onSetZoneInterpolation = useCallback(
        (eventAreaList) =>
            dispatch(recsEventsActions.updateEventDetails(fieldGuid, { eventAreaList })),
        [dispatch, fieldGuid]
    );
    const onSetEventAreaIsIncluded = useCallback(
        (areaId: number, isIncluded: boolean) =>
            dispatch(recsEventsActions.setAreaIsIncluded(fieldGuid, areaId, isIncluded)),
        [dispatch, fieldGuid]
    );

    const [isEditData, setIsEditData] = useState<boolean>(null);
    const [isEditColorRamp, setIsEditColorRamp] = useState<boolean>(null);
    const [isManualAddZone, setIsManualAddZone] = useState<boolean>(null);
    const [isManualEditZone, setIsManualEditZone] = useState<boolean>(null);
    const [hideContext, setHideContext] = useState<boolean>(null);
    const [updateLegend, setUpdateLegend] = useState<boolean>(null);
    const [colorRampGuid, setColorRampGuid] = useState<string>(null);
    const [selectedLayer, setSelectedLayer] = useState<LayerAPI.ILayer>(null);
    const [selectedSurface, setSelectedSurface] = useState<LayerAPI.ISubLayer>(null);
    const hasRestoredDisplaySurface = useRef(false);
    const prevSelectedLayer = useRef<LayerAPI.ILayer>(null);
    const areaInclusionKey =
        eventDetails?.eventAreaList
            ?.map((a) => `${a.eventAreaId}:${a.applyEventToArea ? 1 : 0}`)
            ?.join(",") ?? "";

    useEffect(() => {
        return () => {
            onManualZoneEnabled(false);
        };
    }, []);

    useEffect(() => {
        const haveClassBreaks = eventDetails?.eventAreaList?.some(
            (area) => area.eventAreaClassBreak != null
        );
        if (
            !hasRestoredDisplaySurface.current &&
            isMergeEvent &&
            haveClassBreaks &&
            lastSelectedDisplaySurface &&
            lastSelectedDisplaySurface.fieldGuid === fieldGuid &&
            layerInfos &&
            layerNameToSurfaceInfoMap
        ) {
            const restoredSurface =
                ([...layerNameToSurfaceInfoMap.values()].find(
                    (si) =>
                        si === lastSelectedDisplaySurface.surfaceInfo ||
                        (si.surfaceRendererGuid &&
                            si.surfaceRendererGuid ===
                                lastSelectedDisplaySurface.surfaceInfo.surfaceRendererGuid)
                ) as LayerAPI.ISubLayer) ??
                (lastSelectedDisplaySurface.surfaceInfo as LayerAPI.ISubLayer);
            const restoredLayer = layerInfos.find((lyr) => {
                if (restoredSurface.agEventGeneralGuid) {
                    return lyr.agEventGeneralGuid === restoredSurface.agEventGeneralGuid;
                }
                if (restoredSurface.surfaceGuid && restoredSurface.surfaceTypeGuid) {
                    return (
                        lyr.selectedSurfaceGuid === restoredSurface.surfaceGuid &&
                        lyr.selectedSurfaceTypeGuid === restoredSurface.surfaceTypeGuid
                    );
                }
                if (restoredSurface.analysisLayerGuid) {
                    return lyr.analysisLayerGuid === restoredSurface.analysisLayerGuid;
                }
                return false;
            });
            setSelectedSurface(restoredSurface);
            setSelectedLayer(restoredLayer ?? null);
            const restoredLayerType = restoredLayer && LayerUtilsAPI.getLayerType(restoredLayer);
            setHideContext(
                !restoredLayerType || restoredLayerType === LayerUtilsAPI.LayerType.ANALYSIS
            );
            hasRestoredDisplaySurface.current = true;
        }
    }, [
        isMergeEvent,
        eventDetails,
        lastSelectedDisplaySurface,
        layerInfos,
        layerNameToSurfaceInfoMap,
        fieldGuid,
    ]);

    useEffect(() => {
        hasRestoredDisplaySurface.current = false;
        if (fieldGuid !== recsEventsModel.BATCH_TEMPLATE_FIELD_GUID) {
            onFetchLayerNameToSurfaceInfoMap(fieldGuid);
        }
    }, [fieldGuid, onFetchLayerNameToSurfaceInfoMap]);

    useEffect(() => {
        if (!eventDetails?.eventAreaList) {
            return;
        }
        const haveClassBreaks = eventDetails.eventAreaList.some(
            (area) => area.eventAreaClassBreak != null
        );

        const isManualZonesEdit =
            haveClassBreaks &&
            eventDetails.eventAreaList.some(
                (area) => area.eventAreaClassBreak?.isManualClassifiedYn
            );
        setIsManualEditZone(isManualZonesEdit);

        if (haveClassBreaks) {
            const layerChanged = prevSelectedLayer.current !== selectedLayer;
            prevSelectedLayer.current = selectedLayer;

            if (layerChanged) {
                _updateEventAreaIncluded();
            }
            const type = selectedLayer && LayerUtilsAPI.getLayerType(selectedLayer);

            setHideContext(
                (!selectedLayer || type === LayerUtilsAPI.LayerType.ANALYSIS) && !isManualZonesEdit
            );
        }
    }, [
        fieldGuid,
        selectedLayer,
        areaInclusionKey,
        eventDetails.eventAreaList,
        onSetEventAreaIsIncluded,
    ]);

    useEffect(() => {
        if (updateLegend && selectedLayer) {
            const nextLayer = _getLayerFromInfos(layerInfos);
            const nextSurface = _getSelectedSublayer(nextLayer) as LayerAPI.ISubLayer;
            setDataLoading(true);
            _onSelectDisplayLayer(nextSurface);

            setSelectedLayer(nextLayer);
            setSelectedSurface(nextSurface);
            setUpdateLegend(false);
        }
    }, [
        _getLayerFromInfos,
        _onSelectDisplayLayer,
        layerInfos,
        selectedLayer,
        setDataLoading,
        updateLegend,
    ]);

    useEffect(() => {
        if (colorRamps) {
            const soilTypeColorRamp = colorRamps.find((cr) => cr.colorRampName === "Soil Type");
            onSetZoneColorRamps(colorRamps);
            setColorRampGuid(soilTypeColorRamp?.colorRampGuid);
            onColorRampSelected(soilTypeColorRamp?.colorRampGuid);
        }
    }, [colorRamps]);

    useEffect(() => {
        if (isManualAddZone) {
            onManualZoneEnabled(true);
            _onDisplayManualZones();
        }

        if (isManualEditZone) {
            const soilTypeColorRamp = colorRamps.find((cr) => cr.colorRampName === "Soil Type");
            onManualZoneEnabled(true);

            const zoneCount = eventDetails?.eventAreaList?.length || 1;
            const matchedRamp = colorRamps?.find((cr) => {
                const layerFile = cr?.layerFiles.find((lf) => lf.numberOfClasses === zoneCount);
                return eventDetails?.eventAreaList?.every((area, i) => {
                    return (
                        area.eventAreaClassBreak.red?.toString() ==
                            layerFile?.classBreaks[i].color.r &&
                        area.eventAreaClassBreak.green?.toString() ==
                            layerFile?.classBreaks[i].color.g &&
                        area.eventAreaClassBreak.blue?.toString() ==
                            layerFile?.classBreaks[i].color.b
                    );
                });
            });
            if (matchedRamp) {
                setColorRampGuid(matchedRamp.colorRampGuid);
                onColorRampSelected(matchedRamp.colorRampGuid);
                return;
            } else {
                setColorRampGuid(soilTypeColorRamp?.colorRampGuid);
                onColorRampSelected(soilTypeColorRamp?.colorRampGuid);
            }
        }
    }, [isManualAddZone, isManualEditZone]);

    useEffect(() => {
        if (eventDetails.zoneFileGuid) {
            setIsManualAddZone(false);
            setIsManualEditZone(false);
        }
    }, [eventDetails.zoneFileGuid]);

    useEffect(() => {
        if (isManualAddZone || isManualEditZone) {
            _updateEventAreaIncluded();
        }
    }, [eventDetails.eventAreaList.length]);

    if (isLoading) {
        return null;
    }
    console.assert(eventDetails.eventAreaList.length > 0);
    const area = eventDetails.eventAreaList[0];
    console.assert(area.agEventList.length > 0);
    const { agEventModel } = area.agEventList[0];
    const transactionType = eventDetails.agEventTypeList[0].agEventTransactionTypeName;
    let EditForm = sampleSoilModule.SampleSoilForm;
    if (transactionType.toLowerCase().includes("tissue")) {
        EditForm = sampleTissueModule.SampleTissueForm;
    }

    return (
        <div className="rec-event-info-form sampling-tissue-zone-info">
            {_getZoneClassLegend()}
            <EditForm
                agEventModel={agEventModel as eventsModels.SampleSetup}
                fieldGuid={fieldGuid}
            />
        </div>
    );

    function _updateEventAreaIncluded() {
        eventDetails.eventAreaList.forEach((eventArea) =>
            onSetEventAreaIsIncluded(eventArea.eventAreaId, eventArea.applyEventToArea)
        );
    }

    function _getSelectedSublayer(dataLayer) {
        if (!dataLayer || !dataLayer.subLayers) {
            return null;
        }

        const type = LayerUtilsAPI.getLayerType(dataLayer);
        switch (type) {
            case LayerUtilsAPI.LayerType.EVENT_IMPORTED:
            case LayerUtilsAPI.LayerType.EVENT_FROM_EQUATION_REC:
                return dataLayer.subLayers.find((layer) => {
                    return (
                        layer.importAttributeGuid === dataLayer.selectedAttributeGuid &&
                        layer.surfaceTypeGuid === dataLayer.selectedSurfaceTypeGuid
                    );
                });
            case LayerUtilsAPI.LayerType.ANALYSIS:
                return dataLayer.subLayers.find((layer) => {
                    return layer.analysisLayerGuid === dataLayer.analysisLayerGuid;
                });
            case LayerUtilsAPI.LayerType.MANAGEMENT_AREA:
            case LayerUtilsAPI.LayerType.EVENT_MANUAL:
            case LayerUtilsAPI.LayerType.SOIL:
            case LayerUtilsAPI.LayerType.REC:
                return dataLayer.subLayers.find((layer) => {
                    return (
                        layer.surfaceGuid === dataLayer.selectedSurfaceGuid &&
                        layer.surfaceTypeGuid === dataLayer.selectedSurfaceTypeGuid
                    );
                });
            default:
                return null;
        }
    }

    function _onCloseDataEdit() {
        setIsEditData(false);
    }

    function _onCloseColorRampEdit() {
        setIsEditColorRamp(false);
    }

    function _onSaveColorRampEdit({ colorRampGuid }) {
        _onCloseColorRampEdit();
        setColorRampGuid(colorRampGuid);
        onColorRampSelected(colorRampGuid);
        _onDisplayManualZones();
    }

    function _onSaveDataEdit({
        layer,
        surface,
        attributeGuid,
        classificationMethodGuid,
        colorRampGuid,
        colorSchemeGuid,
        numberOfClasses,
        classBreaks,
        surfaceTypeGuid,
        surfaceGuid,
    }) {
        _onCloseDataEdit();
        setDataLoading(true);
        const newLayer = { ...layer };
        newLayer.selectedSurfaceTypeGuid = surfaceTypeGuid;
        newLayer.selectedAttributeGuid = attributeGuid;
        newLayer.selectedSurfaceGuid = surfaceGuid;
        newLayer.analysisLayerGuid = surface.analysisLayerGuid;

        const type = LayerUtilsAPI.getLayerType(newLayer);
        if (type != LayerUtilsAPI.LayerType.ANALYSIS) {
            if (newLayer.isManual) {
                onChangeManualLayerProps({
                    itemDimIdx: -1,
                    fieldGuid: fieldGuid,
                    layer: newLayer,
                    surface,
                    attributeGuid,
                    colorSchemeGuid,
                });
            } else {
                onChangeSurfaceLayerProps({
                    itemDimIdx: -1,
                    fieldGuid,
                    layer: newLayer,
                    surface: surface,
                    attributeGuid,
                    classificationMethodGuid,
                    colorRampGuid,
                    colorSchemeGuid,
                    numberOfClasses,
                    surfaceTypeGuid,
                    classBreaks,
                });
            }
            setSelectedLayer(newLayer);
            setUpdateLegend(true);
        } else {
            _onSelectDisplayLayer(surface);
            setSelectedLayer(newLayer);
            setSelectedSurface(surface);
        }
    }

    function _getDisplayLayerSelectInput(): JSX.Element {
        if (fieldGuid === recsEventsModel.BATCH_TEMPLATE_FIELD_GUID) {
            return null;
        }
        const options =
            layerNameToSurfaceInfoMap == null
                ? []
                : [...layerNameToSurfaceInfoMap.entries()].map(([layerName, surfaceInfo]) => ({
                      label: layerName,
                      value: surfaceInfo,
                  }));

        const dataLoading =
            (selectedSurface && loadingSurfacesSet.has(selectedSurface.surfaceGuid)) || isLoading;
        const type = selectedLayer && LayerUtilsAPI.getLayerType(selectedLayer);
        const letterIcon = formatMessage(layerMessages.layerTypeEvent);

        return (
            <div className="sample-zone-info-layer-controls">
                <SelectInput
                    containerClassNames={["display-layer-select-input"]}
                    disabled={layerNameToSurfaceInfoMap == null}
                    optionIsHiddenKey={ACTIVE_YN}
                    placeholderText={formatMessage(messages.displayLayerPlaceholderTxt)}
                    options={options}
                    value={selectedSurface}
                    onChange={(selection) => {
                        selection && _onSelectDisplayLayer(selection as LayerAPI.ISubLayer);
                    }}
                />
                <ContextMenu
                    showSampleZoneInfo={() => {
                        setIsEditData(true);
                    }}
                    enableManualZones={() => {
                        setIsManualAddZone(true);
                    }}
                    hideSampleZoneInfo={null}
                />
                {isEditData && (
                    <EditLayerProperties
                        isOpen={isEditData}
                        selectedLayer={selectedLayer}
                        selectedSurface={selectedSurface}
                        showLayerSelect={true}
                        layerInfos={layerInfos}
                        letterIcon={letterIcon}
                        onSave={(evt) => _onSaveDataEdit(evt)}
                        onClose={() => _onCloseDataEdit()}
                        type={type}
                    />
                )}
                {dataLoading ? <Loader /> : null}
            </div>
        );
    }

    function _getInterpolationType(): number {
        const { role } = userInfo;
        if (role.zoneInterpolation) {
            return InterpolationType.ZONE_INTERPOLATION;
        } else if (role.zoneSampling) {
            return InterpolationType.ZONE_SAMPLING;
        } else {
            return InterpolationType.STANDARD;
        }
    }

    function _getInterpolationLabel(interpolationType): string {
        switch (interpolationType) {
            case InterpolationType.ZONE_INTERPOLATION:
                return formatMessage(messages.zoneInterpolationCbLabel);
            case InterpolationType.ZONE_SAMPLING:
                return formatMessage(messages.zoneSamplingCbLabel);
            default:
                return null;
        }
    }

    function _getLayerFromInfos(layerInfos: LayerAPI.ILayer[]): LayerAPI.ILayer {
        const layerType = selectedLayer && LayerUtilsAPI.getLayerType(selectedLayer);

        switch (layerType) {
            case LayerUtilsAPI.LayerType.SOIL:
                return layerInfos.find((layer) => {
                    return layer.soilsFieldGuid === selectedLayer.soilsFieldGuid;
                });
            case LayerUtilsAPI.LayerType.MANAGEMENT_AREA:
            case LayerUtilsAPI.LayerType.ANALYSIS:
                return layerInfos.find((layer) => {
                    return layer.analysisLayerGuid === selectedLayer.analysisLayerGuid;
                });
            case LayerUtilsAPI.LayerType.EVENT_IMPORTED:
            case LayerUtilsAPI.LayerType.EVENT_FROM_EQUATION_REC:
            case LayerUtilsAPI.LayerType.EVENT_MANUAL:
                return layerInfos.find((layer) => {
                    return layer.agEventGeneralGuid === selectedLayer.agEventGeneralGuid;
                });
            case LayerUtilsAPI.LayerType.REC:
                return layerInfos.find((layer) => {
                    return layer.recGeneralGuid === selectedLayer.recGeneralGuid;
                });
            default:
                return null;
        }
    }

    function _getZoneClassLegend(): JSX.Element {
        const haveClassBreaks = eventDetails.eventAreaList.some(
            (area) => area.eventAreaClassBreak != null
        );

        if (!haveClassBreaks && !isManualAddZone) {
            return _getDisplayLayerSelectInput();
        }

        const zoneCount = eventDetails?.eventAreaList?.length || 1;
        const currentColorRamp = isManualAddZone
            ? colorRamps?.find((cr) => cr.colorRampGuid === colorRampGuid)
            : null;

        // find the right class size
        const layerFileToUse = currentColorRamp?.layerFiles.find(
            (lf) => lf.numberOfClasses === zoneCount
        );
        const cbLabelToTotalAcresMap = new Map();
        for (const [i, area] of eventDetails.eventAreaList.entries()) {
            const areaClassBreak = isManualAddZone
                ? layerFileToUse?.classBreaks[i]
                : area.eventAreaClassBreak;
            const { calculatedArea } = area;
            const key = isManualAddZone ? area.eventAreaId : areaClassBreak.label;
            const accum = cbLabelToTotalAcresMap.has(key) ? cbLabelToTotalAcresMap.get(key) : 0;
            cbLabelToTotalAcresMap.set(key, accum + calculatedArea);
        }

        const cbLabelToColorMap = eventDetails.eventAreaList.reduce(
            (cbLabelToColorMap, area, i) => {
                const classBreak = isManualAddZone
                    ? layerFileToUse?.classBreaks[i]
                    : area.eventAreaClassBreak;
                const key = isManualAddZone ? area.eventAreaId : classBreak.label;
                const rgbCssStr = isManualAddZone
                    ? `rgb(${classBreak.color.r}, ${classBreak.color.g}, ${classBreak.color.b})`
                    : classBreak.rgbCssStr;
                if (!cbLabelToColorMap.has(key)) {
                    cbLabelToColorMap.set(key, rgbCssStr);
                }
                return cbLabelToColorMap;
            },
            new Map()
        );

        const legendElements = [...cbLabelToColorMap.keys()].map((label) => {
            const rgbCssStr = cbLabelToColorMap.get(label);
            const legendColorStyle = { backgroundColor: rgbCssStr };
            const zoneSize = formatMessage(commonMessages.zoneSize, {
                calculatedArea: FormattingHelpers.formatNumber(cbLabelToTotalAcresMap.get(label)),
            });

            return (
                <div key={label} className="event-zone-classbreaks-legend">
                    <div className="event-zone-legend-color" style={legendColorStyle} />
                    <div className="event-zone-legend-label">
                        <span className="label-text" title={label}>
                            {label}
                        </span>
                        <span className="label-size"> ({zoneSize})</span>
                    </div>
                </div>
            );
        });
        !isManualAddZone &&
            !isManualEditZone &&
            console.assert(
                new Set(eventDetails.eventAreaList.map((area) => area.eventAreaClassBreak.heading))
                    .size === 1
            );
        const legendHeading =
            isManualAddZone || isManualEditZone
                ? "Manual Zones"
                : eventDetails.eventAreaList[0].eventAreaClassBreak.heading;
        const { interpolationTypeId } = eventDetails.eventAreaList[0].agEventList[0].agEventModel;
        const interpolationType = _getInterpolationType();
        const interpolate =
            interpolationTypeId !== InterpolationType.STANDARD &&
            interpolationTypeId === interpolationType;

        const type = selectedLayer && LayerUtilsAPI.getLayerType(selectedLayer);
        const dataLoading =
            (selectedSurface && loadingSurfacesSet.has(selectedSurface.surfaceGuid)) || isLoading;
        const letterIcon = formatMessage(layerMessages.layerTypeEvent);
        const showCloseOnly =
            hideContext ||
            (isMergeEvent &&
                (interpolationTypeId === InterpolationType.ZONE_SAMPLING ||
                    interpolationTypeId === InterpolationType.ZONE_INTERPOLATION));

        return (
            <div className="zone-legend-container">
                <div className="sample-zone-info-layer-controls">
                    <div className="zone-legend-heading-container sample-zone-info-layer-controls">
                        <div className="zone-legend-heading">{legendHeading}</div>
                        {showCloseOnly ? (
                            <span onClick={_onClose}>
                                <CloseIcon />
                            </span>
                        ) : (
                            <ContextMenu
                                showSampleZoneInfo={() => {
                                    isManualAddZone || isManualEditZone
                                        ? setIsEditColorRamp(true)
                                        : setIsEditData(true);
                                }}
                                hideSampleZoneInfo={_onClose}
                            />
                        )}
                    </div>
                    {isEditData && (
                        <EditLayerProperties
                            isOpen={isEditData}
                            selectedLayer={selectedLayer}
                            selectedSurface={selectedSurface}
                            showLayerSelect={false}
                            layerInfos={layerInfos}
                            letterIcon={letterIcon}
                            onSave={(evt) => _onSaveDataEdit(evt)}
                            onClose={() => _onCloseDataEdit()}
                            type={type}
                        />
                    )}
                    {isEditColorRamp && (
                        <EditColorRamp
                            isOpen={isEditColorRamp}
                            onSave={_onSaveColorRampEdit}
                            onClose={_onCloseColorRampEdit}
                        />
                    )}
                </div>
                {legendElements}
                {interpolationType === InterpolationType.STANDARD ? null : (
                    <Checkbox
                        className="interpolation-cb"
                        label={_getInterpolationLabel(interpolationType)}
                        value={interpolate}
                        onChange={(evt, cbValue) => _onSetZoneInterpolation(cbValue)}
                    />
                )}
                {dataLoading ? <Loader /> : null}
            </div>
        );
    }

    function _onClose(): void {
        onManualZoneEnabled(false);
        _onSetZoneInterpolation(false);
        onResetZones();
        setSelectedLayer(null);
        setSelectedSurface(null);

        setIsManualAddZone(false);
        setIsManualEditZone(false);
    }

    function _onDisplayManualZones(): void {
        _onSetZoneInterpolation(true);
        onSetManualZones(fieldGuid);
    }

    function _onSelectDisplayLayer(surfaceInfo: LayerAPI.ISubLayer): void {
        _onSetZoneInterpolation(true);
        onSetEventZonesFromLayer(surfaceInfo);

        logFirebaseEvent("sampling_display_layer");
        const dataLayer =
            layerInfos &&
            layerInfos.find((lyr) => {
                if (
                    surfaceInfo.agEventGeneralGuid &&
                    surfaceInfo.agEventGeneralGuid === lyr.agEventGeneralGuid
                ) {
                    return true;
                }
                if (
                    surfaceInfo.surfaceGuid &&
                    surfaceInfo.selectedSurfaceTypeGuid &&
                    lyr.selectedSurfaceTypeGuid === surfaceInfo.surfaceTypeGuid &&
                    lyr.selectedSurfaceGuid === surfaceInfo.surfaceGuid
                ) {
                    return true;
                }
                if (
                    surfaceInfo.analysisLayerGuid &&
                    lyr.analysisLayerGuid === surfaceInfo.analysisLayerGuid
                ) {
                    return true;
                }
                return (
                    !surfaceInfo.agEventGeneralGuid &&
                    surfaceInfo.importAttributeGuid &&
                    surfaceInfo.surfaceTypeGuid &&
                    lyr.selectedAttributeGuid === surfaceInfo.importAttributeGuid &&
                    lyr.selectedSurfaceTypeGuid === surfaceInfo.surfaceTypeGuid
                );
            });
        const layerType = dataLayer && LayerUtilsAPI.getLayerType(dataLayer);

        setSelectedLayer(dataLayer);
        setSelectedSurface(surfaceInfo);
        setHideContext(!layerType || layerType === LayerUtilsAPI.LayerType.ANALYSIS);
    }

    function _onSetZoneInterpolation(cbValue): void {
        const interpolationTypeId = cbValue ? _getInterpolationType() : InterpolationType.STANDARD;
        const eventAreaList = eventDetails.eventAreaList.map((eventArea) => {
            return recsEventsModel.AgEventArea.updateAgEventArea(eventArea, {
                agEventList: eventArea.agEventList.map((agEvent) => {
                    return recsEventsModel.AgEvent.updateAgEvent(agEvent, {
                        agEventModel: agEvent.agEventModel.updateAgEventModel({
                            interpolationTypeId: interpolationTypeId,
                        }),
                    });
                }),
            });
        });
        onSetZoneInterpolation(eventAreaList);
    }
};

export const SamplingZoneInfo = SamplingZoneInfo_;
